import {
  Activity,
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  Power,
  PowerOff,
  RefreshCw,
  Server,
  Shield,
  Wifi,
  XCircle,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import IBServiceControlModal from "../../components/admin/IBServiceControlModal";
import { useTheme } from "../../contexts/ThemeContext";
import servicesHealthService, {
  AdvisorHealthEntry,
  IBServiceHealthResponse,
} from "../../services/servicesHealthService";
import { formatNumber } from "../../utils/numberUtils";

// ---------------------------------------------------------------------------
// Shared inline sub-components (unchanged from original)
// ---------------------------------------------------------------------------

const StatusBadge: React.FC<{ status: boolean; trueText?: string; falseText?: string }> = ({
  status,
  trueText = "Yes",
  falseText = "No",
}) => {
  if (status) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        {trueText}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      <XCircle className="w-3 h-3 mr-1" />
      {falseText}
    </span>
  );
};

const ServiceStatusBadge: React.FC<{ status: "ON" | "OFF" }> = ({ status }) => {
  if (status === "ON") {
    return (
      <span
        className="relative inline-flex items-center justify-center w-32 px-4 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white"
        style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.5)" }}
      >
        <span className="absolute left-2 w-2.5 h-2.5 bg-green-300 rounded-full animate-ping opacity-75" />
        <span className="absolute left-2 w-2.5 h-2.5 bg-green-300 rounded-full" />
        <CheckCircle className="w-4 h-4 mr-2 ml-3" />
        ONLINE
      </span>
    );
  }
  return (
    <span
      className="relative inline-flex items-center justify-center w-32 px-4 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-red-500 to-orange-500 text-white"
      style={{ boxShadow: "0 0 20px rgba(239, 68, 68, 0.5)" }}
    >
      <span className="absolute left-2 w-2.5 h-2.5 bg-red-300 rounded-full" />
      <XCircle className="w-4 h-4 mr-2 ml-3" />
      OFFLINE
    </span>
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode; isDarkMode: boolean }> = ({
  label,
  value,
  isDarkMode,
}) => (
  <div className="flex justify-between items-center py-2">
    <span className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>{label}</span>
    <span className={`text-sm font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>{value}</span>
  </div>
);

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const formatUptime = (seconds: number): string => {
  const totalSeconds = Math.floor(seconds);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(" ");
};

const formatMs = (ms: number | null): string => {
  if (ms === null) return "N/A";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${formatNumber(ms / 1000, 1)}s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
};

// ---------------------------------------------------------------------------
// IBServiceCard -- renders the full health card for one advisor IB service
// ---------------------------------------------------------------------------

interface IBServiceCardProps {
  ibHealth: IBServiceHealthResponse;
  advisorAlias: string;
  advisorIbkrAccountId: string;
  isDarkMode: boolean;
  controlLoading: boolean;
  onTurnOn: () => void;
  onTurnOff: () => void;
}

const IBServiceCard: React.FC<IBServiceCardProps> = ({
  ibHealth,
  advisorAlias,
  advisorIbkrAccountId,
  isDarkMode,
  controlLoading,
  onTurnOn,
  onTurnOff,
}) => (
  <div className={`rounded-lg border ${isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"}`}>
    {/* Service Header */}
    <div
      className={`px-6 py-4 border-b ${isDarkMode ? "border-rp-separator" : "border-gray-200"} flex items-center justify-between`}
    >
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${ibHealth.serviceStatus === "ON" ? "bg-green-100" : "bg-red-100"}`}>
          <Server className={`w-6 h-6 ${ibHealth.serviceStatus === "ON" ? "text-green-600" : "text-red-600"}`} />
        </div>
        <div className="ml-4">
          <h2 className={`text-lg font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
            {advisorAlias}
          </h2>
          <p className={`text-sm font-mono ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            {advisorIbkrAccountId}
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <ServiceStatusBadge status={ibHealth.serviceStatus} />
        {ibHealth.serviceStatus === "OFF" ? (
          <button
            onClick={onTurnOn}
            disabled={controlLoading}
            className="group relative w-32 py-2.5 text-sm font-semibold rounded-xl flex items-center justify-center transition-all duration-300 disabled:opacity-50 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 hover:scale-105 active:scale-95"
            style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.4), 0 4px 15px rgba(34, 197, 94, 0.3)" }}
          >
            <span className="absolute inset-0 rounded-xl bg-green-400 opacity-0 group-hover:opacity-30 animate-pulse transition-opacity duration-300" />
            <Power className="w-5 h-5 mr-2 group-hover:animate-pulse" />
            <span className="relative">Power On</span>
          </button>
        ) : (
          <button
            onClick={onTurnOff}
            disabled={controlLoading}
            className="group relative w-32 py-2.5 text-sm font-semibold rounded-xl flex items-center justify-center transition-all duration-300 disabled:opacity-50 bg-gradient-to-r from-red-500 to-orange-600 text-white hover:from-red-600 hover:to-orange-700 hover:scale-105 active:scale-95"
            style={{ boxShadow: "0 0 20px rgba(239, 68, 68, 0.4), 0 4px 15px rgba(239, 68, 68, 0.3)" }}
          >
            <span className="absolute inset-0 rounded-xl bg-red-400 opacity-0 group-hover:opacity-30 animate-pulse transition-opacity duration-300" />
            <PowerOff className="w-5 h-5 mr-2 group-hover:animate-pulse" />
            <span className="relative">Power Off</span>
          </button>
        )}
      </div>
    </div>

    {/* Service Details Grid */}
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Overview Card */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? "bg-rp-black/50 border-rp-separator" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center mb-3">
            <Activity className={`w-5 h-5 mr-2 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
            <h3 className={`text-sm font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>Overview</h3>
          </div>
          <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <InfoRow isDarkMode={isDarkMode} label="Environment" value={ibHealth.environment} />
            <InfoRow isDarkMode={isDarkMode} label="Service IP" value={ibHealth.serviceIpAddress} />
            <InfoRow isDarkMode={isDarkMode} label="Outgoing IP" value={ibHealth.outgoingIp} />
            <InfoRow isDarkMode={isDarkMode} label="Uptime" value={formatUptime(ibHealth.uptime)} />
          </div>
        </div>

        {/* Authentication Card */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? "bg-rp-black/50 border-rp-separator" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center mb-3">
            <Shield className={`w-5 h-5 mr-2 ${isDarkMode ? "text-green-400" : "text-green-600"}`} />
            <h3 className={`text-sm font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>Authentication</h3>
          </div>
          <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <InfoRow isDarkMode={isDarkMode} label="OAuth Token" value={<StatusBadge status={ibHealth.authentication.hasOAuthToken} />} />
            <InfoRow isDarkMode={isDarkMode} label="Session Auth" value={<StatusBadge status={ibHealth.authentication.brokerageSessionAuthenticated} />} />
            <InfoRow isDarkMode={isDarkMode} label="Session Connected" value={<StatusBadge status={ibHealth.authentication.brokerageSessionConnected} />} />
          </div>
        </div>

        {/* Tickle Scheduler Card */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? "bg-rp-black/50 border-rp-separator" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center mb-3">
            <Clock className={`w-5 h-5 mr-2 ${isDarkMode ? "text-purple-400" : "text-purple-600"}`} />
            <h3 className={`text-sm font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>Tickle Scheduler</h3>
          </div>
          <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <InfoRow isDarkMode={isDarkMode} label="Running" value={<StatusBadge status={ibHealth.tickleScheduler.isRunning} />} />
            <InfoRow isDarkMode={isDarkMode} label="Retry Count" value={ibHealth.tickleScheduler.retryCount} />
            <InfoRow isDarkMode={isDarkMode} label="Interval" value={formatMs(ibHealth.tickleScheduler.intervalMs)} />
            <InfoRow isDarkMode={isDarkMode} label="Last Tickle" value={formatMs(ibHealth.authentication.timeSinceLastTickle)} />
          </div>
        </div>

        {/* Reset Event Card */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? "bg-rp-black/50 border-rp-separator" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center mb-3">
            <RefreshCw className={`w-5 h-5 mr-2 ${isDarkMode ? "text-orange-400" : "text-orange-600"}`} />
            <h3 className={`text-sm font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>Reset Event</h3>
          </div>
          <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <InfoRow isDarkMode={isDarkMode} label="Scheduler Running" value={<StatusBadge status={ibHealth.resetEvent.schedulerRunning} />} />
            <InfoRow isDarkMode={isDarkMode} label="In Reset Period" value={<StatusBadge status={ibHealth.resetEvent.isInResetPeriod} trueText="Yes" falseText="No" />} />
            <InfoRow isDarkMode={isDarkMode} label="Daily Reset" value={<StatusBadge status={ibHealth.resetEvent.isDailyReset} />} />
            <InfoRow isDarkMode={isDarkMode} label="Weekly Reset" value={<StatusBadge status={ibHealth.resetEvent.isWeeklyReset} />} />
          </div>
        </div>
      </div>

      {/* IBKR Brokerage Session Details */}
      {ibHealth.ibkrBrokerageSession && (
        <div className={`mt-6 p-4 rounded-lg border ${isDarkMode ? "bg-rp-black/50 border-rp-separator" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center mb-3">
            <Wifi className={`w-5 h-5 mr-2 ${isDarkMode ? "text-cyan-400" : "text-cyan-600"}`} />
            <h3 className={`text-sm font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>IBKR Brokerage Session</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
              <InfoRow isDarkMode={isDarkMode} label="Authenticated" value={<StatusBadge status={ibHealth.ibkrBrokerageSession.authenticated} />} />
              <InfoRow isDarkMode={isDarkMode} label="Connected" value={<StatusBadge status={ibHealth.ibkrBrokerageSession.connected} />} />
            </div>
            <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
              <InfoRow isDarkMode={isDarkMode} label="Competing" value={<StatusBadge status={!ibHealth.ibkrBrokerageSession.competing} trueText="No" falseText="Yes" />} />
              <InfoRow isDarkMode={isDarkMode} label="Message" value={ibHealth.ibkrBrokerageSession.message || "N/A"} />
            </div>
            <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
              <InfoRow isDarkMode={isDarkMode} label="Server Name" value={ibHealth.ibkrBrokerageSession.serverInfo?.serverName || "N/A"} />
              <InfoRow isDarkMode={isDarkMode} label="Server Version" value={ibHealth.ibkrBrokerageSession.serverInfo?.serverVersion || "N/A"} />
            </div>
            <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
              <InfoRow isDarkMode={isDarkMode} label="MAC Address" value={ibHealth.ibkrBrokerageSession.MAC || "N/A"} />
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Reset Periods */}
      {ibHealth.resetEvent.upcomingResetPeriods && ibHealth.resetEvent.upcomingResetPeriods.length > 0 && (
        <div className={`mt-6 p-4 rounded-lg border ${isDarkMode ? "bg-rp-black/50 border-rp-separator" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center mb-3">
            <Calendar className={`w-5 h-5 mr-2 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
            <h3 className={`text-sm font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
              Upcoming Reset Periods
            </h3>
            <span className={`ml-2 text-xs ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>
              (Next {Math.min(5, ibHealth.resetEvent.upcomingResetPeriods.length)} periods)
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-xs ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
                  <th className="pb-2 pr-4 font-medium">UTC</th>
                  <th className="pb-2 pr-4 font-medium">PST (Pacific)</th>
                  <th className="pb-2 pr-4 font-medium">IST (India)</th>
                  <th className="pb-2 font-medium">Duration</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
                {ibHealth.resetEvent.upcomingResetPeriods.slice(0, 5).map((period, periodIndex) => {
                  const startDate = new Date(period.start);
                  const endDate = new Date(period.end);
                  const durationMs = endDate.getTime() - startDate.getTime();
                  const durationMinutes = Math.round(durationMs / 60000);
                  const durationHours = Math.floor(durationMinutes / 60);
                  const remainderMinutes = durationMinutes % 60;
                  const durationStr = durationHours > 0 ? `${durationHours}h ${remainderMinutes}m` : `${remainderMinutes}m`;

                  const formatTimeRange = (start: Date, end: Date, timeZone: string) => {
                    const formatOpts: Intl.DateTimeFormatOptions = {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                      timeZone,
                    };
                    const startStr = start.toLocaleString("en-US", formatOpts);
                    const endStr = end.toLocaleString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                      timeZone,
                    });
                    return `${startStr} - ${endStr}`;
                  };

                  return (
                    <tr key={periodIndex}>
                      <td className={`py-2 pr-4 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
                        {formatTimeRange(startDate, endDate, "UTC")}
                      </td>
                      <td className={`py-2 pr-4 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
                        {formatTimeRange(startDate, endDate, "America/Los_Angeles")}
                      </td>
                      <td className={`py-2 pr-4 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
                        {formatTimeRange(startDate, endDate, "Asia/Kolkata")}
                      </td>
                      <td className={`py-2 text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
                        {durationStr}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div className={`mt-6 p-4 rounded-lg border ${isDarkMode ? "bg-rp-black/50 border-rp-separator" : "bg-gray-50 border-gray-200"}`}>
        <div className="flex items-center mb-3">
          <Clock className={`w-5 h-5 mr-2 ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`} />
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>Timestamps</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <InfoRow isDarkMode={isDarkMode} label="Time (UTC)" value={ibHealth.currentTimeUTC} />
          </div>
          <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <InfoRow isDarkMode={isDarkMode} label="Time (ET)" value={ibHealth.currentTimeET} />
          </div>
          <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <InfoRow isDarkMode={isDarkMode} label="Last Auth" value={formatMs(ibHealth.authentication.timeSinceLastAuth) + " ago"} />
          </div>
          <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <InfoRow isDarkMode={isDarkMode} label="Last Tickle" value={formatMs(ibHealth.authentication.timeSinceLastTickle) + " ago"} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Error card for an advisor whose health check failed
// ---------------------------------------------------------------------------

const AdvisorErrorCard: React.FC<{
  advisorAlias: string;
  advisorIbkrAccountId: string;
  errorMessage: string;
  isDarkMode: boolean;
}> = ({ advisorAlias, advisorIbkrAccountId, errorMessage, isDarkMode }) => (
  <div className={`rounded-lg border ${isDarkMode ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"}`}>
    <div className={`px-6 py-4 flex items-center justify-between`}>
      <div className="flex items-center">
        <div className="p-2 rounded-lg bg-red-100">
          <Server className="w-6 h-6 text-red-600" />
        </div>
        <div className="ml-4">
          <h2 className={`text-lg font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
            {advisorAlias}
          </h2>
          <p className={`text-sm font-mono ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            {advisorIbkrAccountId}
          </p>
        </div>
      </div>
      <ServiceStatusBadge status="OFF" />
    </div>
    <div className={`px-6 pb-4 space-y-1`}>
      <div className="flex items-center">
        <AlertTriangle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
        <p className={`text-sm font-semibold ${isDarkMode ? "text-red-400" : "text-red-700"}`}>
          Service unreachable -- Cloud Run instance is down
        </p>
      </div>
      <p className={`text-xs ml-7 ${isDarkMode ? "text-red-400/70" : "text-red-600/70"}`}>
        {errorMessage}
      </p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

interface ModalTarget {
  advisorIbkrAccountId: string;
  advisorAlias: string;
  mode: "turn-on" | "turn-off";
}

const ServicesHealthPage: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [advisorEntries, setAdvisorEntries] = useState<AdvisorHealthEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const [controlLoading, setControlLoading] = useState(false);
  const [controlError, setControlError] = useState<string | null>(null);
  const [controlSuccess, setControlSuccess] = useState<string | null>(null);
  const [modalTarget, setModalTarget] = useState<ModalTarget | null>(null);

  const fetchAllHealth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const entries = await servicesHealthService.getAllIBServiceHealth();
      setAdvisorEntries(entries);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch IB Service health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllHealth();
  }, [fetchAllHealth]);

  const handleControlConfirm = useCallback(async () => {
    if (!modalTarget) return;
    try {
      setControlLoading(true);
      setControlError(null);
      setControlSuccess(null);
      const response =
        modalTarget.mode === "turn-on"
          ? await servicesHealthService.turnOnIBService(modalTarget.advisorIbkrAccountId)
          : await servicesHealthService.turnOffIBService(modalTarget.advisorIbkrAccountId);
      setControlSuccess(`${modalTarget.advisorAlias}: ${response.data.message}`);
      setModalTarget(null);
      await fetchAllHealth();
    } catch (err) {
      setControlError(
        err instanceof Error
          ? `${modalTarget.advisorAlias}: ${err.message}`
          : `Failed to ${modalTarget.mode === "turn-on" ? "turn on" : "turn off"} IB Service`,
      );
    } finally {
      setControlLoading(false);
    }
  }, [modalTarget, fetchAllHealth]);

  const hasData = advisorEntries.length > 0;

  if (error && !hasData) {
    return (
      <AdminPageLayout
        title="Plutus Services Health"
        description="Monitor the health and status of Plutus backend services"
      >
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div
              className={`p-6 rounded-lg ${
                isDarkMode ? "bg-red-900/20 border border-red-800" : "bg-red-50 border border-red-200"
              }`}
            >
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
                <p className={`text-lg font-medium ${isDarkMode ? "text-red-400" : "text-red-600"}`}>
                  Failed to load service health
                </p>
              </div>
              <p className={`text-sm mb-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>{error}</p>
              <button
                onClick={fetchAllHealth}
                disabled={loading}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-blue-600 text-rp-grey hover:bg-blue-700 disabled:opacity-50"
                    : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                }`}
              >
                {loading ? "Retrying..." : "Retry"}
              </button>
            </div>
          </div>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="Plutus Services Health"
      description="Monitor the health and status of Plutus backend services"
      actions={
        <div className="flex items-center space-x-4">
          {lastRefresh && (
            <span className={`text-xs ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchAllHealth}
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium rounded-lg border flex items-center ${
              isDarkMode
                ? "bg-rp-surface border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised disabled:opacity-50"
                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            }`}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Control Success Banner */}
        {controlSuccess && (
          <div
            className={`p-4 rounded-lg ${
              isDarkMode ? "bg-green-900/20 border border-green-800" : "bg-green-50 border border-green-200"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <p className={`text-sm ${isDarkMode ? "text-green-400" : "text-green-700"}`}>{controlSuccess}</p>
              </div>
              <button
                onClick={() => setControlSuccess(null)}
                className={`text-sm ${isDarkMode ? "text-green-400 hover:text-green-300" : "text-green-600 hover:text-green-700"}`}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Control Error Banner */}
        {controlError && (
          <div
            className={`p-4 rounded-lg ${
              isDarkMode ? "bg-red-900/20 border border-red-800" : "bg-red-50 border border-red-200"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <p className={`text-sm ${isDarkMode ? "text-red-400" : "text-red-700"}`}>{controlError}</p>
              </div>
              <button
                onClick={() => setControlError(null)}
                className={`text-sm ${isDarkMode ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-700"}`}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && !hasData && (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div
                className={`animate-spin rounded-full h-10 w-10 border-b-2 mb-4 ${
                  isDarkMode ? "border-rp-amber" : "border-blue-600"
                }`}
              ></div>
              <p className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>Loading service health...</p>
            </div>
          </div>
        )}

        {/* Advisor IB Service Cards */}
        {advisorEntries.map((entry) =>
          entry.health ? (
            <IBServiceCard
              key={entry.advisorIbkrAccountId}
              ibHealth={entry.health}
              advisorAlias={entry.advisorAlias}
              advisorIbkrAccountId={entry.advisorIbkrAccountId}
              isDarkMode={isDarkMode}
              controlLoading={controlLoading}
              onTurnOn={() =>
                setModalTarget({
                  advisorIbkrAccountId: entry.advisorIbkrAccountId,
                  advisorAlias: entry.advisorAlias,
                  mode: "turn-on",
                })
              }
              onTurnOff={() =>
                setModalTarget({
                  advisorIbkrAccountId: entry.advisorIbkrAccountId,
                  advisorAlias: entry.advisorAlias,
                  mode: "turn-off",
                })
              }
            />
          ) : (
            <AdvisorErrorCard
              key={entry.advisorIbkrAccountId}
              advisorAlias={entry.advisorAlias}
              advisorIbkrAccountId={entry.advisorIbkrAccountId}
              errorMessage={entry.error || "Unknown error"}
              isDarkMode={isDarkMode}
            />
          ),
        )}

        {/* Stale-data warning */}
        {error && hasData && (
          <div
            className={`p-4 rounded-lg ${
              isDarkMode ? "bg-yellow-900/20 border border-yellow-800" : "bg-yellow-50 border border-yellow-200"
            }`}
          >
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
              <p className={`text-sm ${isDarkMode ? "text-yellow-400" : "text-yellow-700"}`}>
                Failed to refresh: {error}. Showing cached data.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Control Modal -- shared across all advisors */}
      {modalTarget && (
        <IBServiceControlModal
          isOpen={true}
          onClose={() => setModalTarget(null)}
          onConfirm={handleControlConfirm}
          mode={modalTarget.mode}
          loading={controlLoading}
          advisorAlias={modalTarget.advisorAlias}
        />
      )}
    </AdminPageLayout>
  );
};

export default ServicesHealthPage;
